import type { DateRangeValue } from "@/components/ui/date-range-picker";
import { lastMonthsRange } from "@/lib/date-utils";
import type { OrderStatus } from "@/services/admin-order.service";
import { createTableFiltersStore } from "./createTableFiltersStore";

/**
 * Sentinel for "no status filter".
 *
 * Radix Select throws on a `<SelectItem value="">` and treats `""` as "nothing selected", so the
 * cleared state needs a real value. No OrderStatus member is `'all'`, so this cannot collide with a
 * genuine status.
 */
export const ALL_STATUSES = "all";

/**
 * Statuses offered by the filter. Every OrderStatus member is here — unlike the tenant list, nothing
 * in this enum is unreachable, so leaving one out would hide rows an operator can see in the table.
 */
export const ORDER_STATUS_OPTIONS: {
  value: OrderStatus | typeof ALL_STATUSES;
  label: string;
}[] = [
  { value: ALL_STATUSES, label: "All statuses" },
  { value: "Draft", label: "Draft" },
  { value: "Pending", label: "Pending" },
  { value: "Paid", label: "Paid" },
  { value: "Processing", label: "Processing" },
  { value: "Complete", label: "Complete" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "Failed", label: "Failed" },
  { value: "Refunded", label: "Refunded" },
];

export interface OrderListFilters extends Record<string, unknown> {
  /**
   * Free-text term: a whole order ID, tenant ID or user ID, a whole order number or e-mail, or a
   * fragment of an order number, e-mail or phone number. The backend routes it by shape.
   */
  keyword: string;

  status: OrderStatus | typeof ALL_STATUSES;

  /** Placed-at range, ISO strings. Sent as fromDate / toDate. */
  dateRange: DateRangeValue;
}

/**
 * True when a stored range has exactly one end. The picker only ever produces both or neither, so
 * this can only come from hand-edited storage.
 */
const isHalfRange = (range: DateRangeValue | undefined): boolean =>
  Boolean(range?.from) !== Boolean(range?.to);

/**
 * Both ends of the placed-at range default to the last six months rather than to "no filter": the
 * list opens on a bounded window, and the operator widens or clears it from the picker. Computed
 * once per page load, so a tab left open for days keeps the window it started with until the filters
 * are reset.
 */
const defaults: OrderListFilters = {
  keyword: "",
  status: ALL_STATUSES,
  dateRange: lastMonthsRange(),
};

/**
 * Filter and paging state for the admin order list, cached in sessionStorage.
 *
 * No `defaultSort`: GetOrdersQuery has no sort parameters and the repository always orders by
 * placed-at descending, so a sort control here would move an arrow without moving the rows.
 *
 * Bump `version` in step with any change to the shape above or to the status options — a cached value
 * the backend no longer accepts comes back as a 400 on a filter the operator cannot see.
 */
export const useOrderFiltersStore = createTableFiltersStore<OrderListFilters>({
  name: "admin.orders.filters",
  // v2 retires `tenantId`. Removing the input was not enough on its own: the value stays in the
  // operator's localStorage and the page went on sending it, filtering the list by a tenant with
  // nothing on screen to say so and no control left to clear it. The one search box answers a
  // pasted tenant id now.
  version: 2,
  defaults,
  sanitize: (filters) => ({
    ...filters,

    // sessionStorage outlives a release: a status that is no longer offered must not survive as a
    // filter the operator cannot see in the UI, and so cannot clear from it. The endpoint answers
    // 400 for a value outside its enum, so this would break the screen outright.
    status: ORDER_STATUS_OPTIONS.some((option) => option.value === filters.status)
      ? filters.status
      : defaults.status,

    // Exactly one end is not a range: the picker cannot produce one, and the query would apply half
    // a window. An *empty* range is left alone — it is the operator having deliberately cleared the
    // six-month default to search all time.
    dateRange: isHalfRange(filters.dateRange) ? defaults.dateRange : filters.dateRange,
  }),
});
